/**
 * The whole portal: 8 screens behind a hash router.
 *
 * WHY HASH ROUTING: invitation links are emailed, so the token travels in the URL. A fragment is never sent to
 * the server and never appears in a Referer header or an access log, and it needs no host rewrite rules — so
 * `portal.autoryn.ai/#/invite/<token>` is both safer and deployable as pure static files.
 */
import { useCallback, useEffect, useState } from 'react'
import { api, GuestApiError, newIdempotencyKey } from './api/client'
import { clearSession, hasSession, setSession } from './session'
import type { Desk, Issue, IssueComment, InvitationView, Workspace, SharedItem, FilePackage, ApprovalRequest, FormTemplate, ClientUpdate } from './api/contract'
import { isSessionDead } from './api/contract'
import { approvalOptions, canDownload, commentBox, invitationState, missingRequired, quoteReady, resolutionCard, resolutionNoteFor, statusLabelFor, updateAcknowledged, validateSubmission } from './screens/logic'
import { useLanguage } from './context/LanguageContext'

type Route =
  | { name: 'landing' }
  | { name: 'invite'; token: string }
  | { name: 'desks' }
  | { name: 'submit'; deskId: string }
  | { name: 'issues' }
  | { name: 'issue'; issueId: string }
  | { name: 'workspaces' }
  | { name: 'workspace'; wsId: string }
  | { name: 'approval'; wsId: string; id: string }
  | { name: 'form'; wsId: string; id: string }
  | { name: 'rfq'; wsId: string; id: string }
  | { name: 'ended' }
  | { name: 'notfound' }

function parseHash(hash: string): Route {
  const p = hash.replace(/^#\/?/, '').split('/').filter(Boolean)
  if (p.length === 0) return hasSession() ? { name: 'issues' } : { name: 'landing' }
  if (p[0] === 'invite' && p[1]) return { name: 'invite', token: decodeURIComponent(p[1]) }
  if (p[0] === 'desks' && !p[1]) return { name: 'desks' }
  if (p[0] === 'desks' && p[2] === 'new') return { name: 'submit', deskId: p[1] }
  if (p[0] === 'issues' && !p[1]) return { name: 'issues' }
  if (p[0] === 'issues' && p[1]) return { name: 'issue', issueId: p[1] }
  if (p[0] === 'workspaces' && !p[1]) return { name: 'workspaces' }
  if (p[0] === 'workspaces' && p[1] && !p[2]) return { name: 'workspace', wsId: p[1] }
  if (p[0] === 'workspaces' && p[1] && p[2] === 'approvals' && p[3]) return { name: 'approval', wsId: p[1], id: p[3] }
  if (p[0] === 'workspaces' && p[1] && p[2] === 'forms' && p[3]) return { name: 'form', wsId: p[1], id: p[3] }
  if (p[0] === 'workspaces' && p[1] && p[2] === 'rfqs' && p[3]) return { name: 'rfq', wsId: p[1], id: p[3] }
  if (p[0] === 'ended') return { name: 'ended' }
  return { name: 'notfound' }
}
const go = (to: string) => { window.location.hash = to }

/** Any failure that means "the session is gone" sends the guest to one honest screen, not a dead end. */
function useGuestCall<T>(fn: () => Promise<T>, deps: unknown[]): { data: T | null; error: string | null; loading: boolean; reload: () => void } {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [tick, setTick] = useState(0)
  const run = useCallback(fn, deps)   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let live = true
    setLoading(true); setError(null)
    run().then(
      (d) => { if (live) { setData(d); setLoading(false) } },
      (e: unknown) => {
        if (!live) return
        setLoading(false)
        const code = e instanceof GuestApiError ? e.code : ''
        if (isSessionDead(code)) { clearSession(); go('/ended'); return }
        setError(e instanceof Error ? e.message : 'Something went wrong on our side.')
      },
    )
    return () => { live = false }
  }, [run, tick])
  return { data, error, loading, reload: () => setTick((t) => t + 1) }
}

const Shell = ({ children, back }: { children: React.ReactNode; back?: { to: string; label: string } }) => {
  const { t, language, setLanguage } = useLanguage()
  return (
    <div className="shell">
      <header>
        <span className="brand">{t('app.brand')}</span>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value as 'en' | 'ar')}
            style={{
              padding: '0.5rem',
              borderRadius: '4px',
              border: '1px solid #ccc',
              cursor: 'pointer',
            }}
          >
            <option value="en">{t('common.english')}</option>
            <option value="ar">{t('common.arabic')}</option>
          </select>
          {hasSession() && (
            <button className="link" onClick={() => { api.logout().catch(() => undefined); clearSession(); go('/ended') }}>{t('app.logout')}</button>
          )}
        </div>
      </header>
      {back && <button className="link back" onClick={() => go(back.to)}>← {back.label}</button>}
      <main>{children}</main>
      <footer>{t('app.footerMessage')}</footer>
    </div>
  )
}

const Loading = () => {
  const { t } = useLanguage()
  return <p className="muted" data-testid="loading">{t('common.loading')}</p>
}

const Problem = ({ text, onRetry }: { text: string; onRetry?: () => void }) => {
  const { t } = useLanguage()
  return (
    <div className="notice error"><p>{text}</p>{onRetry && <button onClick={onRetry}>{t('common.retry')}</button>}</div>
  )
}

// ── 1. Landing ─────────────────────────────────────────────────────────────────
const Landing = () => {
  const { t } = useLanguage()
  return (
    <Shell>
      <h1>{t('landing.title')}</h1>
      <p>{t('landing.description')}</p>
      <p className="muted">{t('landing.lostEmail')}</p>
    </Shell>
  )
}

// ── 2. Invitation ──────────────────────────────────────────────────────────────
function Invitation({ token }: { token: string }) {
  const { t } = useLanguage()
  const { data, error, loading } = useGuestCall<InvitationView>(() => api.invitation(token), [token])
  const [accepting, setAccepting] = useState(false)
  const [acceptError, setAcceptError] = useState<string | null>(null)

  const accept = async () => {
    setAccepting(true); setAcceptError(null)
    try {
      const s = await api.accept(token)
      // The token comes back EXACTLY ONCE and there is no refresh — store it before anything else can fail.
      setSession(s.sessionToken)
      go('/desks')
    } catch (e) {
      setAcceptError(e instanceof Error ? e.message : t('errors.couldNotAccept'))
    } finally { setAccepting(false) }
  }

  if (loading) return <Shell><Loading /></Shell>
  const state = invitationState(data, error ?? undefined)
  return (
    <Shell>
      {state.kind === 'acceptable' && (
        <>
          <h1>{t('invitation.welcome')}</h1>
          <p>{t('invitation.description')}</p>
          {acceptError && <Problem text={acceptError} />}
          <button className="primary" disabled={accepting} onClick={accept}>{accepting ? t('invitation.opening') : t('invitation.openPortal')}</button>
        </>
      )}
      {state.kind === 'accepted' && <><h1>{t('invitation.alreadyOpen')}</h1><p>{t('invitation.alreadyOpenDesc')}</p></>}
      {state.kind === 'expired' && <><h1>{t('invitation.expired')}</h1><p>{t('invitation.expiredDesc')}</p></>}
      {state.kind === 'unavailable' && <><h1>{t('invitation.unavailable')}</h1><p>{t('invitation.unavailableDesc')}</p></>}
      {state.kind === 'error' && <Problem text={state.message} />}
    </Shell>
  )
}

// ── 3. Desks ───────────────────────────────────────────────────────────────────
function Desks() {
  const { t } = useLanguage()
  const { data, error, loading, reload } = useGuestCall<Desk[]>(() => api.desks(), [])
  if (loading) return <Shell><Loading /></Shell>
  if (error) return <Shell><Problem text={error} onRetry={reload} /></Shell>
  const items = data ?? []
  return (
    <Shell>
      <h1>{t('desks.title')}</h1>
      {items.length === 0 && <p className="muted">{t('desks.empty')}</p>}
      <ul className="cards">
        {items.map((d) => (
          <li key={d.deskId}>
            <h2>{d.name}</h2>
            {d.description && <p>{d.description}</p>}
            <button onClick={() => go(`/desks/${d.deskId}/new`)}>{t('desks.reportIssue')}</button>
          </li>
        ))}
      </ul>
      <button className="link" onClick={() => go('/issues')}>{t('desks.seeReported')}</button>
    </Shell>
  )
}

// ── 4. Submit ──────────────────────────────────────────────────────────────────
function Submit({ deskId }: { deskId: string }) {
  const { t } = useLanguage()
  const { data: desk, error, loading } = useGuestCall<Desk>(() => api.desk(deskId), [deskId])
  const [form, setForm] = useState({ title: '', description: '', issueType: '', severity: '', environment: '' })
  const [errs, setErrs] = useState<string[]>([])
  const [sending, setSending] = useState(false)
  // Minted ONCE per form-fill and deliberately NOT regenerated on retry: that is the whole point. If the first
  // attempt reached the server but the response was lost, pressing send again replays the same key and the
  // server returns the issue it already filed instead of creating a second one.
  const [idempotencyKey] = useState(() => newIdempotencyKey())

  useEffect(() => {
    if (desk) setForm((f) => ({ ...f, issueType: f.issueType || desk.issueTypes[0] || '', severity: f.severity || desk.severities[0] || '' }))
  }, [desk])

  if (loading) return <Shell back={{ to: '/desks', label: t('common.back') }}><Loading /></Shell>
  if (error || !desk) return <Shell back={{ to: '/desks', label: t('common.back') }}><Problem text={error ?? t('errors.couldNotOpenDesk')} /></Shell>

  const submit = async () => {
    const problems = validateSubmission(desk, form)
    setErrs(problems)
    if (problems.length) return
    setSending(true)
    try {
      const issue = await api.submitIssue(deskId, {
        title: form.title, description: form.description, issueType: form.issueType,
        // 🔴 `severity`, NOT `severityReported`. `severityReported` is the RESPONSE key — it was mirrored back
        // into the REQUEST, where nothing reads it (`validateIssueInput` reads `body.severity` and is the only
        // reader in the whole tree). So the severity the customer is FORCED to choose was dropped on the wire
        // and the projection stored `undefined` for every issue ever filed from this portal — invisible,
        // because the portal never renders severity back.
        severity: form.severity,
        // The server's replay protection is keyed on this and the portal never sent one, so it minted a fresh
        // id per request and the replay branch was unreachable: a retry after a lost response (mobile drop, a
        // 30s router timeout while the dyno keeps working) filed a SECOND issue and a second internal ticket.
        // Minted once per form-fill, so a retry of the same submission reuses it and the server replays.
        idempotencyKey,
        ...(form.environment ? { environment: form.environment } : {}),
      })
      go(`/issues/${issue.issueId}`)
    } catch (e) {
      setErrs([e instanceof Error ? e.message : t('errors.couldNotSend')])
    } finally { setSending(false) }
  }

  return (
    <Shell back={{ to: '/desks', label: t('submit.backToAllDesks') }}>
      <h1>{t('submit.title')}</h1>
      <p className="muted">{desk.name}</p>
      {errs.length > 0 && <div className="notice error"><ul>{errs.map((e) => <li key={e}>{e}</li>)}</ul></div>}
      <label>{t('submit.titleLabel')}<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={140} /></label>
      <label>{t('submit.descriptionLabel')}<textarea rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={4000} /></label>
      {desk.issueTypes.length > 0 && (
        <label>{t('submit.kindLabel')}<select value={form.issueType} onChange={(e) => setForm({ ...form, issueType: e.target.value })}>
          {desk.issueTypes.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select></label>
      )}
      {desk.severities.length > 0 && (
        <label>{t('submit.severityLabel')}<select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
          {desk.severities.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select></label>
      )}
      {desk.requireEnvironment && (
        <label>{t('submit.environmentLabel')}<input value={form.environment} onChange={(e) => setForm({ ...form, environment: e.target.value })} placeholder={t('submit.environmentPlaceholder')} maxLength={200} /></label>
      )}
      <button className="primary" disabled={sending} onClick={submit}>{sending ? t('submit.sending') : t('submit.send')}</button>
    </Shell>
  )
}

// ── 5. Issues ──────────────────────────────────────────────────────────────────
function Issues() {
  const { t, tp } = useLanguage()
  const { data, error, loading, reload } = useGuestCall<{ issues: Issue[]; truncated: boolean }>(() => api.issues(), [])
  if (loading) return <Shell><Loading /></Shell>
  if (error) return <Shell><Problem text={error} onRetry={reload} /></Shell>
  const items = data?.issues ?? []
  return (
    <Shell>
      <h1>{t('issues.title')}</h1>
      {items.length === 0 && <p className="muted">{t('issues.empty')} <button className="link" onClick={() => go('/desks')}>{t('issues.reportSomething')}</button></p>}
      <ul className="rows">
        {items.map((i) => (
          <li key={i.issueId}>
            <button className="row" onClick={() => go(`/issues/${i.issueId}`)}>
              <span className="ref">{i.reference}</span>
              <span className="title">{i.title}</span>
              <span className={`status s-${i.publicStatus}`}>{statusLabelFor(i)}</span>
              {i.infoRequestOpen && <span className="badge">We asked you something</span>}
            </button>
          </li>
        ))}
      </ul>
      {data?.truncated && (
        // A cap the customer is not told about is indistinguishable from "that is all there is".
        <p className="muted">{tp('issues.truncatedMessage', { count: items.length })}</p>
      )}
      <button className="link" onClick={() => go('/desks')}>Report something new</button>
      {/* The only way into the half of the product that had no screens until now. */}
      <button className="link" onClick={() => go('/workspaces')}>{t('issues.seeShared')}</button>
    </Shell>
  )
}

// ── 6. Issue detail ────────────────────────────────────────────────────────────
function IssueDetail({ issueId }: { issueId: string }) {
  const { t } = useLanguage()
  const { data: issue, error, loading, reload } = useGuestCall<Issue>(() => api.issue(issueId), [issueId])
  const thread = useGuestCall<{ comments: IssueComment[]; locked: boolean }>(() => api.comments(issueId), [issueId])
  const [draft, setDraft] = useState('')
  const [reply, setReply] = useState('')
  const [reopenReason, setReopenReason] = useState('')
  const [busy, setBusy] = useState('')
  const [flash, setFlash] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  if (loading) return <Shell back={{ to: '/issues', label: t('issueDetail.backToIssues') }}><Loading /></Shell>
  if (error || !issue) return <Shell back={{ to: '/issues', label: t('issueDetail.backToIssues') }}><Problem text={error ?? t('errors.couldNotOpen')} onRetry={reload} /></Shell>

  const card = resolutionCard(issue)
  const box = commentBox(issue, thread.data ?? undefined)
  const act = async (what: string, fn: () => Promise<unknown>, done: string) => {
    setBusy(what); setActionError(null)
    try {
      await fn()
      // approve-resolution answers 200 with the ordinary projection and NO "approved" field, so a success is
      // indistinguishable from a no-op unless we say so. This confirmation is session-local by necessity.
      setFlash(done); reload(); thread.reload()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'That did not go through.')
    } finally { setBusy('') }
  }

  return (
    <Shell back={{ to: '/issues', label: t('issueDetail.backToIssues') }}>
      <h1>{issue.title}</h1>
      <p className="muted">{issue.reference} · <span className={`status s-${issue.publicStatus}`}>{statusLabelFor(issue)}</span></p>
      {issue.summary && <p>{issue.summary}</p>}
      {resolutionNoteFor(issue, 'header') && <div className="notice"><p>{resolutionNoteFor(issue, 'header')}</p></div>}
      {flash && <div className="notice ok"><p>{flash}</p></div>}
      {actionError && <Problem text={actionError} />}

      {issue.infoRequestOpen && (
        <section className="ask">
          <h2>{t('issueDetail.weNeedOne')}</h2>
          {issue.infoRequestPrompt && <p>{issue.infoRequestPrompt}</p>}
          <textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)} maxLength={4000} />
          <button className="primary" disabled={busy !== '' || reply.trim().length < 2}
            onClick={() => act('info', () => api.respondToInfoRequest(issueId, reply), t('issueDetail.thankYouAnswer')).then(() => setReply(''))}>
            {busy === 'info' ? t('issueDetail.sending') : t('issueDetail.yourAnswer')}
          </button>
        </section>
      )}

      {card.show && (
        <section className="resolution">
          <h2>{t('issueDetail.resolved')}</h2>
          {resolutionNoteFor(issue, 'card') && <p>{resolutionNoteFor(issue, 'card')}</p>}
          {card.needsReread && <p className="muted">{t('issueDetail.needsReread')}</p>}
          {/* The DURABLE states come first and replace the prompt entirely. Previously the thank-you lived only
              in component state beside a still-live button, so the screen said "we have recorded that" AND
              "please confirm" at the same time, and a reload lost the thank-you and kept the button. */}
          {card.approved && <p className="muted">{t('issueDetail.confirmed')}</p>}
          {card.reopenPending && <p className="muted">{t('issueDetail.reopenPending')}</p>}
          {card.canApprove && (
            <button className="primary" disabled={busy !== ''}
              onClick={() => act('approve', () => api.approveResolution(issueId), t('issueDetail.thankYouResolution'))}>
              {busy === 'approve' ? t('issueDetail.confirmSaving') : t('issueDetail.confirmResolved')}
            </button>
          )}
          {card.canReopen && (
            <details>
              <summary>{t('issueDetail.notResolved')}</summary>
              <textarea rows={3} placeholder={t('issueDetail.notResolvedPlaceholder')} value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} maxLength={2000} />
              <button disabled={busy !== '' || reopenReason.trim().length < 3}
                onClick={() => act('reopen', () => api.requestReopen(issueId, reopenReason), t('issueDetail.thankYouReopen'))}>
                {busy === 'reopen' ? t('issueDetail.reopenSending') : t('issueDetail.askReopen')}
              </button>
            </details>
          )}
        </section>
      )}

      <section className="thread">
        <h2>{t('issueDetail.messagesTitle')}</h2>
        {thread.loading && <Loading />}
        {thread.error && <Problem text={thread.error} onRetry={thread.reload} />}
        <ul className="comments">
          {(thread.data?.comments ?? []).map((c) => (
            <li key={c.commentNumber} className={c.authorType === 'guest' ? 'guest' : 'staff'}>
              <span className="who">{c.authorType === 'guest' ? t('issueDetail.you') : t('issueDetail.ourTeam')}</span>
              <p>{c.body}</p>
            </li>
          ))}
        </ul>
        {box.canComment ? (
          <>
            <textarea rows={3} value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={4000} placeholder={t('issueDetail.addMessage')} />
            <button disabled={busy !== '' || draft.trim().length < 2}
              onClick={() => act('comment', () => api.addComment(issueId, draft), 'Message sent.').then(() => setDraft(''))}>
              {busy === 'comment' ? t('issueDetail.sending') : t('issueDetail.sendMessage')}
            </button>
          </>
        ) : box.locked ? <p className="muted">{t('issueDetail.conversationClosed')}</p>
          : box.needsReread ? <p className="muted">{t('issueDetail.reopenToComment')}</p>
          : <p className="muted">{t('issueDetail.commentsDisabled')}</p>}
      </section>
    </Shell>
  )
}

// ── 9. Workspaces ──────────────────────────────────────────────────────────────
/**
 * 🔴 THE HALF OF THIS PRODUCT THAT HAD NO SCREENS.
 *
 * Files, approvals, forms, quotes and client updates were built on the server, guarded, audited — and
 * unreachable, because no guest response carried an id until 2026-08-09. `issueId` was the only address the
 * API emitted, which is why this portal was a support desk and nothing else.
 *
 * Everything below addresses items by the ids the server now returns. Nothing is constructed client-side.
 */
function Workspaces() {
  const { t } = useLanguage()
  const { data, error, loading, reload } = useGuestCall<Workspace[]>(() => api.workspaces(), [])
  if (loading) return <Shell><Loading /></Shell>
  if (error) return <Shell><Problem text={error} onRetry={reload} /></Shell>
  const items = data ?? []
  return (
    <Shell>
      <h1>{t('workspaces.title')}</h1>
      {items.length === 0 && <p className="muted">{t('workspaces.empty')}</p>}
      <ul className="rows">
        {items.map((w) => (
          <li key={w.workspaceId}>
            <button className="row" onClick={() => go(`/workspaces/${w.workspaceId}`)}>
              <span className="ref">{w.workspaceNumber}</span>
              <span className="title">{w.name}</span>
            </button>
          </li>
        ))}
      </ul>
    </Shell>
  )
}

// ── 10. One workspace ──────────────────────────────────────────────────────────
/**
 * Five lists, each loaded independently.
 *
 * ⚠️ ONE SECTION FAILING MUST NOT BLANK THE PAGE. A guest can legitimately hold a grant on files and none on
 * approvals, and the server answers 403 for the second — so `Promise.all` would turn a normal, expected
 * refusal into "nothing is here". Each section reports itself, and a section the guest cannot see simply
 * says so.
 */
function useSection<T>(fn: () => Promise<T[]>, deps: unknown[]) {
  const r = useGuestCall<T[]>(async () => {
    try { return await fn() } catch (e) {
      // A missing grant is not an error to this screen; it is the answer.
      if (e instanceof GuestApiError && (e.status === 403 || e.status === 404)) return []
      throw e
    }
  }, deps)
  return r
}

function Section({ title, empty, children }: { title: string; empty: boolean; children: React.ReactNode }) {
  const { t } = useLanguage()
  return (
    <section className="ws-section">
      <h2>{title}</h2>
      {empty ? <p className="muted">{t('workspaceHub.filesEmpty')}</p> : <ul className="rows">{children}</ul>}
    </section>
  )
}

function WorkspaceHub({ wsId }: { wsId: string }) {
  const { t, tp } = useLanguage()
  const ws = useGuestCall<Workspace>(() => api.workspace(wsId), [wsId])
  const packages = useSection<FilePackage>(() => api.filePackages(wsId), [wsId])
  const approvals = useSection<ApprovalRequest>(() => api.approvals(wsId), [wsId])
  const forms = useSection<FormTemplate>(() => api.forms(wsId), [wsId])
  const items = useSection<SharedItem>(() => api.sharedItems(wsId), [wsId])
  const updates = useSection<ClientUpdate>(() => api.clientUpdates(wsId), [wsId])

  const [busy, setBusy] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  if (ws.loading) return <Shell back={{ to: '/workspaces', label: t('workspaceHub.backToWorkspaces') }}><Loading /></Shell>
  if (ws.error) return <Shell back={{ to: '/workspaces', label: t('workspaceHub.backToWorkspaces') }}><Problem text={ws.error} onRetry={ws.reload} /></Shell>

  /**
   * 🔴 THE DOWNLOAD IS REQUESTED ONLY WHEN ASKED FOR, never on render: it is a POST that records the access
   * and mints a one-time URL. Firing it while painting a list would log downloads nobody performed and burn
   * a metered grant's use count.
   */
  const download = async (pkg: FilePackage) => {
    setBusy(pkg.filePackageId); setNote(null)
    try {
      const result = await api.recordDownload(pkg.filePackageId)
      if (result.downloadUrlOneTime) window.location.href = result.downloadUrlOneTime
      // `downloadAvailable: false` is a fact about the package, not a failure — say which it is.
      else setNote(t('workspaceHub.noFileAvailable'))
    } catch (e) {
      setNote(e instanceof Error ? e.message : t('errors.couldNotDownload'))
    } finally { setBusy(null) }
  }

  const acknowledge = async (u: ClientUpdate) => {
    setBusy(u.clientUpdateId); setNote(null)
    try { await api.acknowledgeUpdate(u.clientUpdateId); updates.reload() }
    catch (e) { setNote(e instanceof Error ? e.message : t('errors.couldNotConfirm')) }
    finally { setBusy(null) }
  }

  return (
    <Shell back={{ to: '/workspaces', label: t('workspaceHub.backToWorkspaces') }}>
      <h1>{ws.data?.name}</h1>
      {ws.data?.description && <p>{ws.data.description}</p>}
      {note && <div className="notice"><p>{note}</p></div>}

      <Section title={t('workspaceHub.files')} empty={(packages.data ?? []).length === 0}>
        {(packages.data ?? []).map((pkg) => (
          <li key={pkg.filePackageId} className="row static">
            <span className="ref">{pkg.packageNumber}</span>
            <span className="title">{pkg.title}</span>
            <span className="muted">{tp('workspaceHub.files_count', { count: pkg.fileCount })}</span>
            {canDownload(pkg)
              ? <button disabled={busy === pkg.filePackageId} onClick={() => download(pkg)}>
                  {busy === pkg.filePackageId ? t('workspaceHub.preparing') : t('workspaceHub.download')}
                </button>
              // Not a hidden button: the customer is told this is view-only rather than left wondering.
              : <span className="muted">{t('workspaceHub.viewOnly')}</span>}
          </li>
        ))}
      </Section>

      <Section title={t('workspaceHub.approvals')} empty={(approvals.data ?? []).length === 0}>
        {(approvals.data ?? []).map((a) => (
          <li key={a.approvalId}>
            <button className="row" onClick={() => go(`/workspaces/${wsId}/approvals/${a.approvalId}`)}>
              <span className="ref">{a.externalApprovalNumber}</span>
              <span className="title">{a.title}</span>
              {approvalOptions(a).closed && <span className="badge">Done</span>}
            </button>
          </li>
        ))}
      </Section>

      <Section title={t('workspaceHub.forms')} empty={(forms.data ?? []).length === 0}>
        {(forms.data ?? []).map((f) => (
          <li key={f.formId}>
            <button className="row" onClick={() => go(`/workspaces/${wsId}/forms/${f.formId}`)}>
              <span className="ref">{f.externalFormNumber}</span>
              <span className="title">{f.name}</span>
            </button>
          </li>
        ))}
      </Section>

      <Section title={t('workspaceHub.shared')} empty={(items.data ?? []).length === 0}>
        {(items.data ?? []).map((it) => (
          <li key={it.sharedItemId}>
            {/*
              An RFQ is a shared item under a different grant, and the server does not label it as one on this
              projection — so every shared item offers the quote route rather than the portal guessing. The
              quote screen refuses cleanly if this guest holds no submit_quote grant.
            */}
            <button className="row" onClick={() => go(`/workspaces/${wsId}/rfqs/${it.sharedItemId}`)}>
              <span className="ref">{it.sharedItemNumber}</span>
              <span className="title">{it.title}</span>
            </button>
          </li>
        ))}
      </Section>

      <Section title={t('workspaceHub.updates')} empty={(updates.data ?? []).length === 0}>
        {(updates.data ?? []).map((u) => (
          <li key={u.clientUpdateId} className="row static">
            <span className="ref">{u.shareNumber}</span>
            <span className="title">{u.title}</span>
            {updateAcknowledged(u)
              // Read off `acknowledgedAt`, which comes back on every read — not off component state, which
              // is how the issue screen once thanked someone and then asked again on reload.
              ? <span className="muted">{t('workspaceHub.confirmed')}</span>
              : <button disabled={busy === u.clientUpdateId} onClick={() => acknowledge(u)}>{t('workspaceHub.confirmSeen')}</button>}
          </li>
        ))}
      </Section>
    </Shell>
  )
}

// ── 11. Approval ───────────────────────────────────────────────────────────────
function ApprovalScreen({ wsId, id }: { wsId: string; id: string }) {
  const { t, tp } = useLanguage()
  const { data, error, loading, reload } = useGuestCall<ApprovalRequest[]>(() => api.approvals(wsId), [wsId])
  const [sent, setSent] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  if (loading) return <Shell><Loading /></Shell>
  if (error) return <Shell><Problem text={error} onRetry={reload} /></Shell>
  const a = (data ?? []).find((x) => x.approvalId === id)
  if (!a) return <Shell back={{ to: `/workspaces/${wsId}`, label: t('common.back') }}><Problem text="That request is no longer available." /></Shell>
  const { options, closed, reason } = approvalOptions(a)

  const respond = async (response: string) => {
    setBusy(true); setProblem(null)
    try { await api.respondToApproval(a.approvalId, response); setSent(response) }
    catch (e) { setProblem(e instanceof Error ? e.message : t('errors.couldNotRespond')) }
    finally { setBusy(false) }
  }

  return (
    <Shell back={{ to: `/workspaces/${wsId}`, label: t('common.back') }}>
      <h1>{a.title}</h1>
      <p className="ref">{a.externalApprovalNumber}</p>
      {a.description && <p>{a.description}</p>}
      {a.responseRequiredBy && !closed && <p className="muted">{tp('approval.respondBy', { date: new Date(a.responseRequiredBy).toLocaleDateString() })}</p>}
      {problem && <Problem text={problem} />}
      {sent
        ? <div className="notice"><p>{tp('approval.thankYou', { response: sent })}</p></div>
        : closed
          ? <p className="muted">{reason}</p>
          : (
            <div className="actions">
              {/* 🔴 BUTTONS FROM THE SERVER'S OWN LIST. A guessed approve/reject pair means half of them 4xx. */}
              {options.map((o) => (
                <button key={o} disabled={busy} onClick={() => respond(o)}>{o.replace(/_/g, ' ')}</button>
              ))}
            </div>
          )}
    </Shell>
  )
}

// ── 12. Form ───────────────────────────────────────────────────────────────────
function FormScreen({ wsId, id }: { wsId: string; id: string }) {
  const { t, tp } = useLanguage()
  const { data, error, loading, reload } = useGuestCall<FormTemplate[]>(() => api.forms(wsId), [wsId])
  const [values, setValues] = useState<Record<string, string>>({})
  const [problem, setProblem] = useState<string | null>(null)
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  if (loading) return <Shell><Loading /></Shell>
  if (error) return <Shell><Problem text={error} onRetry={reload} /></Shell>
  const f = (data ?? []).find((x) => x.formId === id)
  if (!f) return <Shell back={{ to: `/workspaces/${wsId}`, label: t('common.back') }}><Problem text="That form is no longer available." /></Shell>

  const submit = async () => {
    // Mirrors the server's own `buildAnswers` rule, so a rejected POST becomes an inline message naming the
    // field instead of a generic refusal.
    const missing = missingRequired(f, values)
    if (missing.length) { setProblem(tp('form.fillIn', { fields: missing.join(', ') })); return }
    setBusy(true); setProblem(null)
    try { await api.submitForm(f.formId, values); setSent(true) }
    catch (e) { setProblem(e instanceof Error ? e.message : t('errors.couldNotSubmitForm')) }
    finally { setBusy(false) }
  }

  if (sent) return (
    <Shell back={{ to: `/workspaces/${wsId}`, label: t('common.back') }}>
      <h1>{f.name}</h1>
      <div className="notice"><p>{t('form.thankYou')}</p></div>
    </Shell>
  )

  return (
    <Shell back={{ to: `/workspaces/${wsId}`, label: t('common.back') }}>
      <h1>{f.name}</h1>
      <p className="ref">{f.externalFormNumber}</p>
      {f.description && <p>{f.description}</p>}
      {problem && <Problem text={problem} />}
      {/* Rendered FROM THE TEMPLATE: a hand-written form would send fieldIds the server silently drops. */}
      {(f.fields ?? []).map((field) => (
        <label key={field.fieldId} className="field">
          <span>{field.label}{field.required ? ' *' : ''}</span>
          {Array.isArray(field.options) && field.options.length > 0 ? (
            <select value={values[field.fieldId] ?? ''} onChange={(e) => setValues((v) => ({ ...v, [field.fieldId]: e.target.value }))}>
              <option value="">{t('form.choose')}</option>
              {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input
              value={values[field.fieldId] ?? ''}
              onChange={(e) => setValues((v) => ({ ...v, [field.fieldId]: e.target.value }))}
            />
          )}
        </label>
      ))}
      <button disabled={busy} onClick={submit}>{busy ? t('form.sending') : t('form.send')}</button>
    </Shell>
  )
}

// ── 13. Quote against a shared item (RFQ) ──────────────────────────────────────
function QuoteScreen({ wsId, id }: { wsId: string; id: string }) {
  const { t, tp } = useLanguage()
  const { data, error, loading, reload } = useGuestCall<SharedItem[]>(() => api.sharedItems(wsId), [wsId])
  const [form, setForm] = useState({ quoteTitle: '', totalAmount: '', currency: '', notes: '' })
  const [problem, setProblem] = useState<string | null>(null)
  const [sent, setSent] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  if (loading) return <Shell><Loading /></Shell>
  if (error) return <Shell><Problem text={error} onRetry={reload} /></Shell>
  const it = (data ?? []).find((x) => x.sharedItemId === id)
  if (!it) return <Shell back={{ to: `/workspaces/${wsId}`, label: t('common.back') }}><Problem text="That item is no longer available." /></Shell>

  const submit = async () => {
    if (!quoteReady(form)) { setProblem(t('quote.titleRequired')); return }
    setBusy(true); setProblem(null)
    try {
      const r = await api.submitQuote(it.sharedItemId, {
        quoteTitle: form.quoteTitle,
        // Empty strings are omitted rather than sent: the server parses money only when a value is present,
        // and an empty one would be a validation failure for a field the supplier chose not to fill in.
        ...(form.totalAmount ? { totalAmount: form.totalAmount, currency: form.currency || 'USD' } : {}),
        ...(form.notes ? { notes: form.notes } : {}),
      })
      setSent(r.supplierQuoteSubmissionNumber || r.reference || 'sent')
    } catch (e) { setProblem(e instanceof Error ? e.message : t('errors.couldNotSubmitQuote')) }
    finally { setBusy(false) }
  }

  return (
    <Shell back={{ to: `/workspaces/${wsId}`, label: t('common.back') }}>
      <h1>{it.title}</h1>
      <p className="ref">{it.sharedItemNumber}</p>
      {it.summary && <p>{it.summary}</p>}
      {it.content && <p>{it.content}</p>}
      {Array.isArray(it.limitations) && it.limitations.length > 0 && (
        <p className="muted">{it.limitations.join(' · ')}</p>
      )}
      {sent
        ? <div className="notice"><p>{tp('quote.thankYou', { reference: sent !== 'sent' ? ` (${sent})` : '' })}</p></div>
        : (
          <>
            <h2>{t('quote.sendQuote')}</h2>
            {problem && <Problem text={problem} />}
            <label className="field"><span>{t('quote.title')} *</span>
              <input value={form.quoteTitle} onChange={(e) => setForm((f) => ({ ...f, quoteTitle: e.target.value }))} /></label>
            <label className="field"><span>{t('quote.total')}</span>
              <input value={form.totalAmount} onChange={(e) => setForm((f) => ({ ...f, totalAmount: e.target.value }))} /></label>
            <label className="field"><span>{t('quote.currency')}</span>
              <input value={form.currency} placeholder={t('quote.currencyPlaceholder')} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} /></label>
            <label className="field"><span>{t('quote.notes')}</span>
              <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></label>
            <button disabled={busy} onClick={submit}>{busy ? t('quote.sending') : t('quote.send')}</button>
          </>
        )}
    </Shell>
  )
}

// ── 7 & 8. Session ended / not found ───────────────────────────────────────────
const Ended = () => {
  const { t } = useLanguage()
  return (
    <Shell>
      <h1>{t('ended.title')}</h1>
      <p>{t('ended.description')}</p>
      <p className="muted">{t('ended.lostLink')}</p>
    </Shell>
  )
}

const NotFound = () => {
  const { t } = useLanguage()
  return (
    <Shell>
      <h1>{t('notFound.title')}</h1>
      <button className="link" onClick={() => go(hasSession() ? '/issues' : '/')}>{t('notFound.goBack')}</button>
    </Shell>
  )
}

export default function App() {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash))
  useEffect(() => {
    const onHash = () => setRoute(parseHash(window.location.hash))
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  // Every session-only screen refuses to render without a token, rather than firing a doomed request.
  if (['desks', 'submit', 'issues', 'issue', 'workspaces', 'workspace', 'approval', 'form', 'rfq'].includes(route.name) && !hasSession()) return <Ended />

  switch (route.name) {
    case 'landing': return <Landing />
    case 'invite': return <Invitation token={route.token} />
    case 'desks': return <Desks />
    case 'submit': return <Submit deskId={route.deskId} />
    case 'issues': return <Issues />
    case 'issue': return <IssueDetail issueId={route.issueId} />
    case 'workspaces': return <Workspaces />
    case 'workspace': return <WorkspaceHub wsId={route.wsId} />
    case 'approval': return <ApprovalScreen wsId={route.wsId} id={route.id} />
    case 'form': return <FormScreen wsId={route.wsId} id={route.id} />
    case 'rfq': return <QuoteScreen wsId={route.wsId} id={route.id} />
    case 'ended': return <Ended />
    default: return <NotFound />
  }
}
